/**
 * ElectronBridge Component
 * Electron 메인 프로세스와 React 앱 간의 통신을 처리
 */

import { useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  setModelInfo,
  setMultiModelData,
  addMultiModelData,
  addOrReplaceAttrs,
  setFocusInfo,
  setModelLoading,
  setPersistLoaded
} from '@/store/base/commonSlice';
import { isElectronEnvironment } from '@/utils/hooks/useElectron';
import { uid } from 'uid';
import EventBus from 'utils/hooks/EventBus';
import { migrateModelPayload, SchemaTooNewError } from 'services/modelSchema';

/**
 * Electron 환경에서 파일 열기/가져오기 이벤트를 처리하는 컴포넌트
 * App.js에서 최상위 레벨에서 렌더링되어야 함
 */
const ElectronBridge = () => {
  const dispatch = useDispatch();

  /**
   * 모델 배열을 처리하는 헬퍼 함수
   * ModelImportActions.js의 setMultipleModelData와 동일한 방식으로 처리
   */
  const processModelArray = useCallback((models, openMethod = 'import') => {
    // 로딩 시작
    dispatch(setModelLoading(true));

    // 스키마 판정은 **여기 한 곳에서** 한다. loadModelData 의 네 갈래(배열/multiModelData/
    // models/단일)가 모두 이 함수로 모이므로, 새 갈래가 생겨도 검사가 빠지지 않는다.
    // (화면이나 갈래마다 막으면 경로가 늘 때 빠뜨린다 — openflow 가 같은 이유로 쓰기 차단을
    //  근원에 두었다.)
    const blocked = [];
    const checkedModels = models.map((model) => {
      try {
        return migrateModelPayload(model, { label: model?.modelName }).model;
      } catch (e) {
        if (e instanceof SchemaTooNewError) {
          console.warn('🚫 스키마 버전이 더 높아 열지 않음:', e.message);
          blocked.push(model?.modelName || '이름 없음');
          return null;
        }
        throw e;
      }
    }).filter(Boolean);

    if (blocked.length > 0) {
      EventBus.emit(
        'SHOW-MSG',
        `${blocked.map((n) => `'${n}'`).join(', ')} 은(는) 현재 프로그램보다 새로운 형식이라 열지 못했습니다. 최신 버전에서 열어 주세요.`
      );
    }
    if (checkedModels.length === 0) {
      dispatch(setModelLoading(false));
      return;
    }

    // 각 모델에 고유 키 부여 (기존 키가 없거나 빈 문자열인 경우)
    const modelsWithKeys = checkedModels.map(model => ({
      ...model,
      openMethod: openMethod,
      key: model.key || uid(10)
    }));

    // focusInfo 초기화
    dispatch(setFocusInfo({
      focusArea: '',
      focusName: '',
      focusDescription: '',
      entityId: '',
      attrId: ''
    }));

    // 기존 모델 데이터를 교체
    dispatch(setMultiModelData(modelsWithKeys));

    // 첫 번째 모델을 활성 모델로 설정
    if (modelsWithKeys.length > 0) {
      const firstModel = modelsWithKeys[0];
      dispatch(setModelInfo(firstModel));

      // attrs가 있으면 로드
      if (firstModel.attrs && Array.isArray(firstModel.attrs)) {
        dispatch(addOrReplaceAttrs(firstModel.attrs));
      }
    }

    // persistLoaded를 true로 설정 (ReactFlow 렌더링 조건 충족)
    dispatch(setPersistLoaded(true));

    // 로딩 완료 (다이어그램 렌더링 시작)
    setTimeout(() => {
      dispatch(setModelLoading(false));
    }, 100);

  }, [dispatch]);

  /**
   * 모델 데이터를 Redux store에 로드
   * JSON 파일 구조를 분석하여 적절한 방식으로 로드
   */
  const loadModelData = useCallback((data) => {

    if (!data) {
      console.warn('⚠️ ElectronBridge: 모델 데이터가 없습니다');
      return;
    }

    try {
      // Case 1: multiModelData 배열인 경우 (여러 모델)
      if (Array.isArray(data)) {
        processModelArray(data, 'import');
        return;
      }

      // Case 2: multiModelData 프로퍼티가 있는 객체인 경우
      if (data.multiModelData && Array.isArray(data.multiModelData)) {
        processModelArray(data.multiModelData, 'import');
        return;
      }

      // Case 2.5: models 프로퍼티가 있는 객체인 경우 (다중 모델 내보내기 형식)
      // { version, exportDate, modelCount, models: [...] }
      if (data.models && Array.isArray(data.models)) {
        processModelArray(data.models, '파일열기');
        return;
      }

      // Case 3: 단일 modelInfo 객체인 경우 (nodes, edges, attrs 포함)
      if (data.nodes || data.edges || data.key || data.modelName) {

        // modelInfo 구조로 변환
        const modelInfo = {
          key: data.key || uid(10),
          modelName: data.modelName || '가져온 모델',
          modelDescription: data.modelDescription || '',
          openMethod: 'import',
          nodes: data.nodes || [],
          edges: data.edges || [],
          attrs: data.attrs || [],
          viewport: data.viewport || {},
          modelConfig: data.modelConfig || {},
          mdlrmdlId: data.mdlrmdlId || '',
        };


        processModelArray([modelInfo], 'import');
        return;
      }

      // Case 4: modelInfo 프로퍼티가 있는 경우
      if (data.modelInfo) {
        const modelInfo = {
          ...data.modelInfo,
          key: data.modelInfo.key || uid(10),
          openMethod: 'import'
        };
        processModelArray([modelInfo], 'import');
        return;
      }

      console.warn('⚠️ ElectronBridge: 알 수 없는 데이터 구조', Object.keys(data));
    } catch (error) {
      console.error('❌ ElectronBridge: 모델 데이터 로드 실패', error);
    }
  }, [processModelArray]);

  /**
   * Electron 이벤트 리스너 등록
   */
  useEffect(() => {
    // Electron 환경이 아니면 무시
    if (!isElectronEnvironment()) {
      return;
    }

    const electronAPI = window.electronAPI;

    // 파일 열기 이벤트 핸들러
    const handleFileOpened = (data) => {
      const { modelData } = data;
      loadModelData(modelData);
    };

    // JSON 가져오기 이벤트 핸들러
    const handleJsonImported = (data) => {
      const { jsonData } = data;
      loadModelData(jsonData);
    };

    // 메뉴 액션 이벤트 핸들러
    const handleMenuAction = (action) => {
      // 메뉴 액션을 커스텀 이벤트로 전파
      window.dispatchEvent(new CustomEvent('electron-menu-action', {
        detail: { action }
      }));
    };

    // 이벤트 리스너 등록
    electronAPI.onFileOpened(handleFileOpened);
    electronAPI.onJsonImported(handleJsonImported);
    electronAPI.onMenuAction(handleMenuAction);

    // 클린업
    return () => {
      // Note: Electron preload에서 removeListener가 구현되어 있어야 함
      if (electronAPI.removeFileOpenedListener) {
        electronAPI.removeFileOpenedListener(handleFileOpened);
      }
      if (electronAPI.removeJsonImportedListener) {
        electronAPI.removeJsonImportedListener(handleJsonImported);
      }
      if (electronAPI.removeMenuActionListener) {
        electronAPI.removeMenuActionListener(handleMenuAction);
      }
    };
  }, [loadModelData]);

  // 이 컴포넌트는 UI를 렌더링하지 않음
  return null;
};

export default ElectronBridge;
